import { useCallback } from 'react';
import { toast } from 'sonner';

import type { CsvImportStatus } from '@/lib/csv-import/api';

import {
  type AttioConfirmedMapping,
  type AttioMappingAction,
  type CreateImportResult,
  describeAttioError,
  describeConfirmError,
  discoverAttioTypes,
  type DiscoveredObjectType,
  type MappingProposalsResult,
  startAttioImport,
  startAttioMappingProposals,
  validateAttioKey,
  waitForAttioJob,
} from './api';
import { reviewRowKey } from './AttioReviewStep';
import { reconcileSelectedTypes } from './attioWizardHelpers';
import type { AttioWizardStateController } from './useAttioWizardState';

/**
 * The three step onBeforeNext/onConfirm async handlers (network calls +
 * polling). Split out of the state hook because these need `orgId` /
 * `onImportCreated` on top of the shared wizard state, and depend on stable
 * individual fields of `state` (never the whole object) so their own
 * `useCallback` identities stay stable exactly as they did pre-split.
 */
export function useAttioWizardStepHandlers({
  orgId,
  onImportCreated,
  state,
}: {
  orgId: number;
  onImportCreated: (importId: number, status: CsvImportStatus) => void;
  state: AttioWizardStateController;
}) {
  const {
    stateRef,
    controllerRef,
    setConnectError,
    setDiscoveredTypes,
    setStandardAvailability,
    setSelectedTypes,
  } = state;
  const {
    setProposalsLoadError,
    setProposalsByEntity,
    setTruncatedByEntity,
    setTotalRecordsByEntity,
    setActionByKey,
    handleClose,
  } = state;

  const handleConnectBeforeNext = useCallback(async () => {
    setConnectError(null);
    // Snapshot the key once so validation and discovery operate on the SAME
    // credential. stateRef.current.apiKey tracks the live input (the Connect
    // field stays editable through the "Verifying…" state), so reading it
    // separately for each await would let a keystroke landing between them
    // validate one key but discover against another.
    const { apiKey } = stateRef.current;
    try {
      const res = await validateAttioKey(orgId, apiKey);
      if (!res.valid) {
        setConnectError('That API key is invalid or expired.');
        throw new Error('invalid-key');
      }
    } catch (err) {
      // The invalid-key sentinel already set a specific message above; for
      // every other failure surface one so the user isn't silently stuck on
      // the Connect step. describeAttioError covers both ApiError (e.g.
      // the 502 network copy) and non-ApiError rejections like offline /
      // request timeout (falling back to a generic message).
      if (!(err instanceof Error && err.message === 'invalid-key')) {
        setConnectError(describeAttioError(err));
      }
      throw err;
    }

    // Discovery is best-effort: a failure here must not block advancing to
    // Configure. Built-in object types still import fine without it, so we
    // just proceed with an empty list — the user never sees an error for
    // this.
    //
    // Whatever discovery returns (or fails to), reconcile the current
    // selection against it: drop any selected custom (`org_...`) type that's
    // no longer in the newly-discovered set — and all of them on failure
    // (empty set) — so a stale selection can't submit an `org_...` name against
    // a schema the user can no longer see. Built-in selections are preserved.
    let discoveredTypes: DiscoveredObjectType[];
    let standardAvailability: Record<string, boolean> | null;
    try {
      const discoverRes = await discoverAttioTypes(orgId, apiKey);
      discoveredTypes = discoverRes.custom_object_types;
      // Null when the backend predates the field: unknown availability keeps
      // every built-in enabled, and the backend's workspace guard covers it.
      standardAvailability = discoverRes.standard_object_types ?? null;
    } catch {
      discoveredTypes = [];
      standardAvailability = null;
    }
    setDiscoveredTypes(discoveredTypes);
    setStandardAvailability(standardAvailability);
    setSelectedTypes(prev =>
      reconcileSelectedTypes(prev, discoveredTypes, standardAvailability)
    );
  }, [
    orgId,
    stateRef,
    setConnectError,
    setDiscoveredTypes,
    setStandardAvailability,
    setSelectedTypes,
  ]);

  // Fetches the engine's proposed property mappings when leaving Configure,
  // pre-selecting each row to the engine's proposed_action. Never rethrows --
  // if the request fails we still advance to Review (with the load-error
  // notice) rather than leaving the user stuck on Configure.
  const handleConfigureBeforeNext = useCallback(async () => {
    const controller = new AbortController();
    controllerRef.current = controller;
    setProposalsLoadError(false);
    try {
      // Dispatch + poll. Awaiting inside onBeforeNext keeps the wizard's own
      // isSubmitting spinner on the button for the whole wait, so the async
      // boundary needs no extra UI — and unlike the old inline call, a real
      // org's fetch is no longer racing a 30s request timeout.
      const { job_id } = await startAttioMappingProposals(
        orgId,
        {
          apiKey: stateRef.current.apiKey,
          selectedObjectTypes: stateRef.current.selectedTypes,
        },
        controller.signal
      );
      const job = await waitForAttioJob(orgId, job_id, controller.signal);
      const res = job.result as unknown as MappingProposalsResult;
      const initial: Record<string, AttioMappingAction> = {};
      for (const proposals of Object.values(res.proposals)) {
        for (const proposal of proposals) {
          initial[reviewRowKey(proposal.entity_type_name, proposal.raw_key)] =
            proposal.proposed_action;
        }
      }
      setProposalsByEntity(res.proposals);
      setTruncatedByEntity(res.truncated);
      setTotalRecordsByEntity(res.total_records);
      setActionByKey(initial);
    } catch (err) {
      controller.abort();
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User cancelled via the polling status view — the wizard is already closing
        // and its state reset by handleClose. Skip the load-error notice
        // (setting it here would survive the reset and wrongly show on the
        // next open). Rethrow so the wizard's onBeforeNext rejection path keeps
        // us on Configure rather than advancing to Review as the wizard tears
        // down — swallowing the abort here resolves onBeforeNext and lets the
        // step advance.
        throw err;
      }
      setProposalsByEntity(null);
      setTruncatedByEntity(null);
      setTotalRecordsByEntity(null);
      setActionByKey({});
      setProposalsLoadError(true);
    }
  }, [
    orgId,
    stateRef,
    controllerRef,
    setProposalsLoadError,
    setProposalsByEntity,
    setTruncatedByEntity,
    setTotalRecordsByEntity,
    setActionByKey,
  ]);

  // Returning the promise engages the wizard's isSubmitting double-submit
  // guard, preventing duplicate imports while the (synchronous, can take
  // many seconds) backend create call is in flight. On failure we do NOT
  // reset — apiKey/selectedTypes survive so the user can retry without
  // re-pasting.
  const handleConfirm = useCallback(async () => {
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const { proposalsByEntity, proposalsLoadError, actionByKey } =
        stateRef.current;
      const confirmedMappings: AttioConfirmedMapping[] | undefined =
        proposalsLoadError ||
        !proposalsByEntity ||
        Object.keys(proposalsByEntity).length === 0
          ? undefined
          : Object.values(proposalsByEntity)
              .flat()
              .map(proposal => ({
                raw_key: proposal.raw_key,
                entity_type_name: proposal.entity_type_name,
                action:
                  actionByKey[
                    reviewRowKey(proposal.entity_type_name, proposal.raw_key)
                  ] ?? proposal.proposed_action,
                target_property_name: proposal.target_property_name,
                field_type_name: proposal.field_type_name,
                is_multi: proposal.is_multi ?? false,
              }));

      const { job_id } = await startAttioImport(
        orgId,
        {
          apiKey: stateRef.current.apiKey,
          selectedObjectTypes: stateRef.current.selectedTypes,
          ...(confirmedMappings ? { confirmedMappings } : {}),
        },
        controller.signal
      );
      const job = await waitForAttioJob(orgId, job_id, controller.signal);
      const res = job.result as unknown as CreateImportResult;
      if (res?.warning) {
        toast.warning(res.warning);
      }
      if (job.csv_import_id === null) {
        // Succeeded without an import id should be impossible — the create task
        // sets it in the same write that marks the job succeeded. Fail loudly
        // rather than handing the CSV modal a null and rendering an empty shell.
        throw new Error('Attio import finished without an import id');
      }
      onImportCreated(job.csv_import_id, res.status);
      handleClose();
    } catch (err) {
      controller.abort();
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User cancelled via the polling status view during the create poll — the
        // wizard is already closing and its state reset by handleClose. This is
        // a deliberate abort, not a failure, so skip the error toast (and don't
        // rethrow it as one). Mirrors the AbortError guard in
        // handleConfigureBeforeNext.
        return;
      }
      toast.error(describeConfirmError(err));
      throw err;
    }
  }, [orgId, onImportCreated, handleClose, stateRef, controllerRef]);

  return { handleConnectBeforeNext, handleConfigureBeforeNext, handleConfirm };
}
